<template>
  <div class="burton-picker">
    <div class="burton-picker__backdrop" aria-hidden="true" />
    <div class="burton-picker__container">
      <header class="burton-picker__header">
        <p class="burton-picker__eyebrow">Burton Data</p>
        <h1 class="burton-picker__title">选择数据看板</h1>
        <p class="burton-picker__subtitle">
          内购小程序与正式小程序数据相互独立，请选择要进入的看板版本。
        </p>
        <p v-if="lastVariant && countdown > 0" class="burton-picker__hint">
          已记住上次选择，{{ countdown }} 秒后将自动进入
          <strong>{{ lastVariantLabel }}</strong>
        </p>
      </header>

      <div class="burton-picker__grid">
        <BurtonVariantOption
          title="内购小程序"
          description="现有内购正式数据看板，访问、加购、支付与订单核心指标。"
          :highlights="['今日实时榜与近7天历史', '员工/好友订单排行', '支持多维度导出']"
          :icon="ShoppingCart"
          accent="#2563eb"
          :is-last-choice="lastVariant === BURTON_VARIANT_IAP"
          :is-counting-down="lastVariant === BURTON_VARIANT_IAP && countdown > 0"
          :countdown="countdown"
          @select="goToVariant(BURTON_VARIANT_IAP)"
        />
        <BurtonVariantOption
          title="正式小程序"
          description="正式小程序数据看板（功能建设中，可先进入占位页）。"
          :highlights="['独立埋点与订单口径', '与内购看板配置分离', '后续按业务需求扩展']"
          :icon="Promotion"
          accent="#0d9488"
          :is-last-choice="lastVariant === BURTON_VARIANT_OFFICIAL"
          :is-counting-down="lastVariant === BURTON_VARIANT_OFFICIAL && countdown > 0"
          :countdown="countdown"
          @select="goToVariant(BURTON_VARIANT_OFFICIAL)"
        />
      </div>

      <footer class="burton-picker__footer">
        <el-button text @click="router.push('/')">返回工具合集</el-button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Promotion, ShoppingCart } from '@element-plus/icons-vue'
import BurtonVariantOption from './components/BurtonVariantOption.vue'
import {
  BURTON_VARIANT_IAP,
  BURTON_VARIANT_OFFICIAL,
  getBurtonDashboardRoute,
  getLastBurtonDashboardVariant,
  setLastBurtonDashboardVariant
} from './burtonDashboardStorage'

const AUTO_ENTER_SECONDS = 2

const router = useRouter()
const lastVariant = ref(getLastBurtonDashboardVariant())
const countdown = ref(0)
let countdownTimer = null
let navigated = false

const lastVariantLabel = computed(() => {
  if (lastVariant.value === BURTON_VARIANT_OFFICIAL) return '正式小程序看板'
  if (lastVariant.value === BURTON_VARIANT_IAP) return '内购小程序看板'
  return ''
})

function clearCountdown() {
  if (countdownTimer) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function goToVariant(variant, { replace = false } = {}) {
  if (navigated) return
  navigated = true
  clearCountdown()
  setLastBurtonDashboardVariant(variant)
  router.push({ path: getBurtonDashboardRoute(variant), replace })
}

function startAutoEnter() {
  if (!lastVariant.value) return
  countdown.value = AUTO_ENTER_SECONDS
  countdownTimer = setInterval(() => {
    countdown.value -= 1
    if (countdown.value <= 0) {
      clearCountdown()
      goToVariant(lastVariant.value, { replace: true })
    }
  }, 1000)
}

onMounted(() => {
  startAutoEnter()
})

onBeforeUnmount(() => {
  clearCountdown()
})
</script>

<style scoped>
.burton-picker {
  position: relative;
  min-height: 100vh;
  padding: 48px 20px 64px;
  overflow: hidden;
  background: #0f172a;
}

.burton-picker__backdrop {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 50% at 50% -10%, rgba(59, 130, 246, 0.35), transparent),
    radial-gradient(ellipse 60% 40% at 100% 80%, rgba(13, 148, 136, 0.2), transparent),
    linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
  pointer-events: none;
}

.burton-picker__container {
  position: relative;
  max-width: 1080px;
  margin: 0 auto;
}

.burton-picker__header {
  margin-bottom: 36px;
  text-align: center;
  color: #f8fafc;
}

.burton-picker__eyebrow {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #94a3b8;
}

.burton-picker__title {
  margin: 0;
  font-size: clamp(28px, 4vw, 36px);
  font-weight: 800;
  letter-spacing: -0.03em;
}

.burton-picker__subtitle {
  margin: 12px auto 0;
  max-width: 520px;
  font-size: 15px;
  line-height: 1.65;
  color: #cbd5e1;
}

.burton-picker__hint {
  margin: 18px auto 0;
  padding: 10px 18px;
  max-width: 480px;
  font-size: 14px;
  line-height: 1.5;
  color: #fde68a;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.35);
  border-radius: 999px;
}

.burton-picker__hint strong {
  color: #fef3c7;
  font-weight: 700;
}

.burton-picker__grid {
  display: grid;
  gap: 24px;
}

@media (min-width: 900px) {
  .burton-picker__grid {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }
}

.burton-picker__footer {
  margin-top: 32px;
  text-align: center;
}

.burton-picker__footer :deep(.el-button) {
  color: #94a3b8;
}

.burton-picker__footer :deep(.el-button:hover) {
  color: #e2e8f0;
}
</style>
